import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addMonths,
  addWeeks,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  subMonths,
  subWeeks,
} from 'date-fns';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  Star,
  Users,
  X,
} from 'lucide-react';
import { useLocationContext } from '../../components/Layout';
import { WeekNavigator } from '../../components/WeekNavigator';
import { dashboardApi } from '../../services/api';
import { getWeekBounds } from '../../utils/overviewDashboardMetrics';
import DashboardCard from './DashboardCard';
import EmployeeAvatar from './EmployeeAvatar';
import { MonthNavigator } from './MonthNavigator';
import {
  defaultFilterForTab,
  filtersForTab,
  SPOTLIGHT_TABS,
} from './spotlightCatalog';
import type {
  SpotlightFilterId,
  SpotlightRankedRow,
  SpotlightTabId,
} from './types';

const AVATAR_COLORS = ['bg-blue-500', 'bg-pink-500', 'bg-violet-500', 'bg-teal-500', 'bg-amber-500', 'bg-indigo-500'];
const SPOTLIGHT_PREVIEW_LIMIT = 5;
/** Initial fetch — only what the card shows. Full list loads when View All opens. */
const SPOTLIGHT_PREVIEW_FETCH_LIMIT = 5;
const SPOTLIGHT_VIEW_ALL_FETCH_LIMIT = 100;
/** Wait for Overview period-metrics before starting so we don't starve week nav. */
const SPOTLIGHT_DEFER_MS = 400;

interface EmployeeSpotlightProps {
  dashboardWeek: Date;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  loading?: boolean;
  /** When false, spotlight waits (period-metrics / KPIs first). */
  metricsReady?: boolean;
  /** Incremented by the dashboard Refresh button so rankings recompute immediately. */
  refreshNonce?: number;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
}

function mapSpotlightRows(
  apiRows: Array<{
    employeeId: number;
    rank: number;
    name: string;
    profileImageUrl?: string;
    updatedAt?: string;
    role: string;
    primaryValue: string;
    primaryNumeric: number;
    secondaryText?: string;
    documentLabel?: string;
    href: string;
  }>,
): SpotlightRankedRow[] {
  return (apiRows ?? []).map((r) => ({
    id: r.employeeId,
    rank: r.rank,
    name: r.name,
    initials: initialsFor(r.name),
    avatarColor: AVATAR_COLORS[r.employeeId % AVATAR_COLORS.length],
    profileImageUrl: r.profileImageUrl,
    updatedAt: r.updatedAt,
    role: r.role,
    primaryValue: r.primaryValue,
    primaryNumeric: r.primaryNumeric,
    secondaryText: r.secondaryText,
    documentLabel: r.documentLabel,
    href: r.href || `/employees/${r.employeeId}`,
  }));
}

const EmployeeSpotlight: React.FC<EmployeeSpotlightProps> = ({
  dashboardWeek,
  weekStartsOn,
  loading: dashboardLoading,
  metricsReady = true,
  refreshNonce = 0,
}) => {
  const navigate = useNavigate();
  const { debouncedSelectedLocationId } = useLocationContext();

  const [tab, setTab] = useState<SpotlightTabId>('attendance');
  const [filterId, setFilterId] = useState<SpotlightFilterId>('top-attendance');
  // Seed spotlight week once from dashboard; do NOT re-sync on every Overview week
  // click — that stacked 4× spotlight calls (~20s each) and starved period-metrics.
  const [selectedWeek, setSelectedWeek] = useState(dashboardWeek);
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(dashboardWeek));
  const seededFromDashboard = useRef(false);
  useEffect(() => {
    if (seededFromDashboard.current) return;
    seededFromDashboard.current = true;
    setSelectedWeek(dashboardWeek);
    setSelectedMonth(startOfMonth(dashboardWeek));
  }, [dashboardWeek]);

  const [rows, setRows] = useState<SpotlightRankedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllModal, setShowAllModal] = useState(false);
  const [viewAllLoaded, setViewAllLoaded] = useState(false);
  const [attendanceDetail, setAttendanceDetail] = useState<{
    loading: boolean;
    error: string | null;
    employeeName: string;
    data: Awaited<ReturnType<typeof dashboardApi.getSpotlightDetails>> | null;
  } | null>(null);

  const weekBounds = useMemo(
    () => getWeekBounds(selectedWeek, weekStartsOn),
    [selectedWeek, weekStartsOn],
  );

  const isRecognition = tab === 'recognition';

  const { fromDate, toDate } = useMemo(() => {
    if (isRecognition) {
      return {
        fromDate: format(startOfMonth(selectedMonth), 'yyyy-MM-dd'),
        toDate: format(endOfMonth(selectedMonth), 'yyyy-MM-dd'),
      };
    }
    return {
      fromDate: format(weekBounds.weekStart, 'yyyy-MM-dd'),
      toDate: format(weekBounds.weekEnd, 'yyyy-MM-dd'),
    };
  }, [isRecognition, selectedMonth, weekBounds.weekStart, weekBounds.weekEnd]);

  useEffect(() => {
    const next = defaultFilterForTab(tab);
    setFilterId(next.id);
  }, [tab]);

  useEffect(() => {
    setShowAllModal(false);
    setAttendanceDetail(null);
    setViewAllLoaded(false);
  }, [filterId, fromDate, toDate, debouncedSelectedLocationId, tab]);

  const tabFilters = useMemo(() => filtersForTab(tab), [tab]);
  const activeFilterLabel = useMemo(
    () => tabFilters.find((f) => f.id === filterId)?.label ?? 'Employee Spotlight',
    [tabFilters, filterId],
  );

  // Latch: unlock once, so week-nav metricsReady flicker does not re-fire spotlight.
  const [spotlightUnlocked, setSpotlightUnlocked] = useState(false);
  useEffect(() => {
    if (metricsReady) setSpotlightUnlocked(true);
  }, [metricsReady]);

  useEffect(() => {
    if (!spotlightUnlocked) return;

    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await dashboardApi.getSpotlight({
          filter: filterId,
          fromDate,
          toDate,
          locationId: debouncedSelectedLocationId,
          limit: SPOTLIGHT_PREVIEW_FETCH_LIMIT,
          signal: controller.signal,
        });
        if (cancelled) return;
        setRows(mapSpotlightRows(data.rows ?? []));
        setViewAllLoaded(false);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        console.error('Spotlight load failed:', err);
        setError('Unable to load spotlight rankings.');
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const start = () => {
      if (cancelled) return;
      void load();
    };

    // Refresh must hit the API immediately; first paint still waits so KPIs go first.
    if (refreshNonce > 0) {
      start();
    } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(start, { timeout: SPOTLIGHT_DEFER_MS });
    } else {
      timeoutId = setTimeout(start, SPOTLIGHT_DEFER_MS);
    }

    return () => {
      cancelled = true;
      controller.abort();
      if (idleId !== undefined && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [filterId, fromDate, toDate, debouncedSelectedLocationId, spotlightUnlocked, refreshNonce]);

  useEffect(() => {
    if (!showAllModal || viewAllLoaded) return;
    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      try {
        const data = await dashboardApi.getSpotlight({
          filter: filterId,
          fromDate,
          toDate,
          locationId: debouncedSelectedLocationId,
          limit: SPOTLIGHT_VIEW_ALL_FETCH_LIMIT,
        });
        if (cancelled) return;
        setRows(mapSpotlightRows(data.rows ?? []));
        setViewAllLoaded(true);
      } catch (err) {
        console.error('Spotlight view-all load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [
    showAllModal,
    viewAllLoaded,
    filterId,
    fromDate,
    toDate,
    debouncedSelectedLocationId,
  ]);

  useEffect(() => {
    if (!showAllModal && !attendanceDetail) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (attendanceDetail) setAttendanceDetail(null);
      else setShowAllModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAllModal, attendanceDetail]);

  const isCompliance = tab === 'compliance' && (filterId === 'docs-expiring' || filterId === 'docs-expired');
  const isRecognitionRating =
    isRecognition &&
    (filterId === 'employee-of-the-month' || filterId === 'highest-rating');

  const previewRows = useMemo(() => rows.slice(0, SPOTLIGHT_PREVIEW_LIMIT), [rows]);
  // Until View All expands the fetch, assume there may be more than the preview.
  const showViewAll = rows.length >= SPOTLIGHT_PREVIEW_LIMIT;

  const openEmployeeProfile = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openRowDetails = async (row: SpotlightRankedRow) => {
    setAttendanceDetail({
      loading: true,
      error: null,
      employeeName: row.name,
      data: null,
    });
    try {
      const data = await dashboardApi.getSpotlightDetails({
        employeeId: row.id,
        filter: filterId,
        fromDate,
        toDate,
      });
      setAttendanceDetail({
        loading: false,
        error: null,
        employeeName: data.name || row.name,
        data,
      });
    } catch (err) {
      console.error('Spotlight details failed:', err);
      setAttendanceDetail({
        loading: false,
        error: 'Unable to load details for this employee.',
        employeeName: row.name,
        data: null,
      });
    }
  };

  const handleRowClick = (row: SpotlightRankedRow) => {
    if (tab === 'attendance' || tab === 'labour') {
      void openRowDetails(row);
      return;
    }
    openEmployeeProfile(row.href);
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'Absent':
        return 'bg-red-50 text-red-700';
      case 'Late':
        return 'bg-amber-50 text-amber-700';
      case 'EarlyOut':
        return 'bg-orange-50 text-orange-700';
      case 'Present':
      case 'BreakOk':
        return 'bg-emerald-50 text-emerald-700';
      case 'Overtime':
      case 'Hours':
      case 'Cost':
      case 'Scheduled':
        return 'bg-blue-50 text-blue-700';
      case 'MissedBreak':
        return 'bg-red-50 text-red-700';
      case 'Swap':
      case 'ExtraShift':
      case 'Accept':
        return 'bg-violet-50 text-violet-700';
      default:
        return 'bg-slate-50 text-slate-700';
    }
  };

  const renderRow = (row: SpotlightRankedRow, denser = false) => (
    <button
      key={`${row.id}-${row.rank}`}
      type="button"
      onClick={() => handleRowClick(row)}
      className={`w-full flex items-center gap-3 text-left hover:bg-gray-50/80 rounded-md ${
        denser ? 'px-5 py-3.5' : 'px-1 py-2.5'
      }`}
    >
      <span className="w-5 text-xs font-semibold text-gray-400 tabular-nums">{row.rank}</span>
      <EmployeeAvatar
        profileImageUrl={row.profileImageUrl}
        updatedAt={row.updatedAt}
        initials={row.initials}
        avatarColor={row.avatarColor}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
        <p className="text-xs text-gray-500 truncate">{row.role}</p>
      </div>
      <div className="flex-shrink-0 text-right max-w-[45%]">
        {isCompliance ? (
          <div className="flex items-center gap-2 justify-end">
            <FileText className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800 truncate">
                {row.documentLabel ?? 'Document'}
              </p>
              <p className="text-[11px] text-amber-600 truncate">{row.primaryValue}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </div>
        ) : isRecognitionRating ? (
          <div className="flex items-center gap-1.5 justify-end">
            <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
            <div>
              <p className="text-sm font-semibold text-gray-900">{row.primaryValue}</p>
              <p className="text-[10px] text-gray-500">{row.secondaryText ?? 'Avg. Rating'}</p>
            </div>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 justify-end">
            <div>
              <p className="text-sm font-semibold text-gray-900">{row.primaryValue}</p>
              {row.secondaryText ? (
                <p className="text-[10px] text-gray-500">{row.secondaryText}</p>
              ) : null}
            </div>
            {tab === 'attendance' || tab === 'labour' ? (
              <ChevronRight className="h-4 w-4 text-gray-300" />
            ) : null}
          </div>
        )}
      </div>
    </button>
  );

  if (dashboardLoading || !spotlightUnlocked) {
    return (
      <DashboardCard title="Employee Spotlight" className="h-full">
        <div className="px-5 pb-5 h-64 animate-pulse bg-gray-50 rounded-lg" />
      </DashboardCard>
    );
  }

  return (
    <>
      <DashboardCard
        title="Employee Spotlight"
        className="h-full flex flex-col"
        action={
          <button
            type="button"
            onClick={() => navigate('/employees')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            View Full Report
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        }
      >
        <div className="px-5 flex flex-col flex-1 min-h-0">
          <div className="flex items-center gap-1 border-b border-gray-100 mb-3 overflow-x-auto">
            {SPOTLIGHT_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    active
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-xs font-medium text-gray-500 flex-shrink-0">Filter:</span>
              <div className="relative min-w-0 flex-1 max-w-xs">
                <select
                  value={filterId}
                  onChange={(e) => setFilterId(e.target.value as SpotlightFilterId)}
                  className="appearance-none w-full text-xs text-gray-800 border border-gray-200 rounded-md pl-2.5 pr-7 py-1.5 bg-white hover:bg-gray-50"
                >
                  {tabFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {isRecognition ? (
              <MonthNavigator
                month={selectedMonth}
                onPreviousMonth={() => setSelectedMonth((m) => subMonths(m, 1))}
                onNextMonth={() => setSelectedMonth((m) => addMonths(m, 1))}
                onMonthSelect={(m: Date) => setSelectedMonth(startOfMonth(m))}
                variant="subtle"
                className="flex-shrink-0"
              />
            ) : (
              <WeekNavigator
                weekStart={weekBounds.weekStart}
                weekEnd={weekBounds.weekEnd}
                weekStartsOn={weekStartsOn}
                currentWeek={selectedWeek}
                onPreviousWeek={() => setSelectedWeek((w) => subWeeks(w, 1))}
                onNextWeek={() => setSelectedWeek((w) => addWeeks(w, 1))}
                onCurrentWeek={() => setSelectedWeek(dashboardWeek)}
                onWeekSelect={(ymd) => {
                  if (ymd) setSelectedWeek(parseISO(ymd));
                }}
                variant="subtle"
                showCurrentWeekButton={false}
                dateFormat="long"
                className="flex-shrink-0"
              />
            )}
          </div>

          {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading rankings…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-gray-500 gap-2">
              <Users className="h-8 w-8 text-gray-300" />
              <p className="text-sm">No employees match this filter for the selected period.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50 -mx-1">
              {previewRows.map((row) => renderRow(row))}
            </div>
          )}

          {showViewAll ? (
            <div className="py-3 border-t border-gray-100 mt-auto">
              <button
                type="button"
                onClick={() => setShowAllModal(true)}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View All Employees
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>
      </DashboardCard>

      {showAllModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="spotlight-all-title"
          onClick={() => setShowAllModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="min-w-0">
                <h3 id="spotlight-all-title" className="text-base font-semibold text-gray-900">
                  {activeFilterLabel}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {rows.length} employee{rows.length === 1 ? '' : 's'} for the selected period
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAllModal(false)}
                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {rows.map((row) => renderRow(row, true))}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowAllModal(false)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {attendanceDetail ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="spotlight-attendance-title"
          onClick={() => setAttendanceDetail(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="min-w-0">
                <h3 id="spotlight-attendance-title" className="text-base font-semibold text-gray-900">
                  {attendanceDetail.employeeName}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {activeFilterLabel}
                  {attendanceDetail.data
                    ? ` · ${format(parseISO(attendanceDetail.data.fromDate), 'd MMM')} – ${format(parseISO(attendanceDetail.data.toDate), 'd MMM yyyy')}`
                    : ''}
                </p>
                {attendanceDetail.data ? (
                  <p className="text-sm text-gray-800 mt-2">
                    <span className="font-semibold">{attendanceDetail.data.summaryPrimary}</span>
                    <span className="text-gray-500"> · {attendanceDetail.data.summarySecondary}</span>
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setAttendanceDetail(null)}
                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {attendanceDetail.loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading details…
                </div>
              ) : attendanceDetail.error ? (
                <p className="px-5 py-8 text-sm text-red-600 text-center">{attendanceDetail.error}</p>
              ) : !attendanceDetail.data?.details?.length ? (
                <p className="px-5 py-8 text-sm text-gray-500 text-center">
                  No details for this period.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {attendanceDetail.data.details.map((day) => (
                    <div
                      key={day.date}
                      className={`px-5 py-3.5 ${day.highlight ? 'bg-amber-50/40' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{day.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{day.secondaryText}</p>
                        </div>
                        <span
                          className={`flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${statusBadgeClass(day.status)}`}
                        >
                          {day.statusLabel || day.primaryText}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setAttendanceDetail(null)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EmployeeSpotlight;
